import { useMemo } from "react";

import { SearchDataMissingError } from "@/app/lib/errors/SearchDataMissingError";
import { PRICE_RANGE } from "@/app/lib/fallbackHotelsFiltersValues";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { useHotelSearchProgress } from "@/features/hotels/hooks/useHotelSearchProgress";
import {
  searchHotelDataMapper,
  useHotelSearchTokens,
} from "@/features/hotels/hooks/useHotelSearchTokens";
import type { InitiateHotelsSearchBodyInputDto } from "@/types/codegen";

const useHotelSearchBase = (searchData: InitiateHotelsSearchBodyInputDto) => {
  const tokensQuery = useHotelSearchTokens(searchData);
  const searchProgressQuery = useHotelSearchProgress(tokensQuery.data || null);

  const progressData = searchProgressQuery.data?.data;
  const progress = Number(progressData?.progress || 0);

  const progressDataMemoized = useMemo(() => {
    if (progressData) {
      return {
        boardBasis: progressData.boardBasis,
        countOffers: progressData.countOffers,
        countHotels: progressData.countHotels,
        minPrice: progressData.minPrice || PRICE_RANGE[0],
        maxPrice: progressData.maxPrice || PRICE_RANGE[1],
        stars: progressData.stars,
      };
    }
  }, [progressData]);

  return {
    searchResultKey: tokensQuery.data?.searchResultKey,
    searchResultToken: tokensQuery.data?.searchResultToken,
    progress,
    progressData: progressDataMemoized,
    isFetching:
      tokensQuery.isFetching ||
      searchProgressQuery.isFetching ||
      progress < 100,
  };
};

export const useHotelSearch = () => {
  const { searchData } = useHotelSearchDataContext();

  if (!searchData) {
    throw new SearchDataMissingError("Hotel search data is missing");
  }

  return useHotelSearchBase(searchHotelDataMapper(searchData));
};
